import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  AnalyticsPeriod,
  MediaType,
  OverviewQuestion,
  ProfContentPerformanceRow,
} from '../../models/analytics.types';
import {
  ContentMetricsAnalyticsResponse,
  ContentMetricsListCriteria,
  ContentMetricsRow,
  ContentMetricsSummary,
  ContentMetricsTangramOption,
  ContentMetricsTypeRollup,
  ProfessorAnalyticsService,
} from '../../services/professor-analytics.service';
import { PAGE_SIZE } from '../../models/period-trends.const';

// Keys the per-row table can be sorted by. Constrained so the template
// can't pass a key the sorter doesn't recognise.
type ContentSortKey =
  | 'title'
  | 'type'
  | 'pathwayTitle'
  | 'views'
  | 'completionRate'
  | 'avgTimeSpent'
  | 'rating'
  | 'interactions';

// Sentinel for the "All Tangrams" option in the dropdown. Keeps the
// HTML simple — `<option value="">` would also work but is less explicit.
const ALL_TANGRAMS = '';

@Component({
  selector: 'tangram-pa-content-performance-tab',
  templateUrl: './content-performance-tab.component.html',
  styleUrls: ['./content-performance-tab.component.scss']
})
export class ContentPerformanceTabComponent implements OnInit, OnChanges, OnDestroy {
  // Period flows down from the parent so the dashboard's period dropdown
  // refetches every tab in lockstep.
  @Input() period: AnalyticsPeriod = 'This Month';
  @Input() startDate = '';
  @Input() endDate   = '';

  loading = true;
  // Used during page changes — the table swaps rows without the
  // tab-level skeleton flashing.
  pageLoading = false;
  error = '';

  // Filters owned by this tab.
  selectedPathwayId: string = ALL_TANGRAMS;
  selectedMediaType: string = 'all';

  // Backend payload, broken out into the slices the template binds.
  tangrams: ContentMetricsTangramOption[] = [];
  // Server-paginated. `contentMetrics` and `mediaEngagement` are page
  // slices; their respective `…Total` fields drive the pagination strip.
  contentMetrics: ContentMetricsRow[] = [];
  contentMetricsTotal = 0;
  mediaEngagement: ContentMetricsRow[] = [];
  mediaEngagementTotal = 0;
  mediaTypePerformance: ContentMetricsTypeRollup[] = [];
  topPerformingContent: ContentMetricsRow[] = [];
  lowPerformingContent: ContentMetricsRow[] = [];
  topQuestions: OverviewQuestion[] = [];
  summary: ContentMetricsSummary = {
    totalMedia: 0,
    totalViews: 0,
    avgCompletionRate: 0,
    avgRating: 0,
  };
  criteria: ContentMetricsListCriteria = {
    minViewsForRanking: 3,
    topCompletionThreshold: 70,
    lowCompletionThreshold: 40,
    listSize: 5,
    topQuestionsLimit: 5,
    topQuestionsGroupedTotal: 0,
    topQuestionsRawTotal: 0,
  };

  // Sort + pagination state for the per-media table.
  contentSortField: ContentSortKey = 'views';
  contentSortDir: 'asc' | 'desc' = 'desc';
  readonly PAGE_SIZE = PAGE_SIZE;
  contentMetricsPage = 1;
  mediaEngagementPage = 1;
  mediaTypePage = 1;

  // Active subscription tracker so a fast period switch doesn't race
  // an in-flight request and overwrite fresh data with stale.
  private fetchSub?: Subscription;
  private routeSub?: Subscription;

  constructor(
    private analyticsService: ProfessorAnalyticsService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Honour ?pathwayId= on first paint and keep the filter across reload /
    // back-forward (same pattern as the AI Insights tab).
    const preselectId = (this.route.snapshot.queryParams['pathwayId'] || '').toString();
    this.selectedPathwayId = preselectId;

    this.routeSub = this.route.queryParams.subscribe((params) => {
      const pid = (params['pathwayId'] || '').toString();
      if (pid !== this.selectedPathwayId) {
        this.selectedPathwayId = pid;
        this.contentMetricsPage = 1;
        this.mediaEngagementPage = 1;
        this.load();
      }
    });

    this.load();
  }

  ngOnDestroy(): void {
    if (this.fetchSub) { this.fetchSub.unsubscribe(); }
    if (this.routeSub) { this.routeSub.unsubscribe(); }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['period'] || changes['startDate'] || changes['endDate']) &&
        !(changes['period'] && changes['period'].firstChange)) {
      // Period change → reset to page 1; the row set is brand new.
      this.contentMetricsPage = 1;
      this.mediaEngagementPage = 1;
      this.load();
    }
  }

  // ── Filter handlers ─────────────────────────────────────────────
  // Switching tangram refetches because the per-media list is server-side
  // scoped (so the per-type rollup and the top/low callouts also reflect
  // only the chosen tangram).
  onPathwayChange(pathwayId: string): void {
    this.selectedPathwayId = pathwayId;
    this.contentMetricsPage = 1;
    this.mediaEngagementPage = 1;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pathwayId: pathwayId || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.load();
  }

  onMediaTypeChange(type: string): void {
    this.selectedMediaType = type;
    this.contentMetricsPage = 1;
    this.mediaEngagementPage = 1;
  }

  // Pagination handlers — re-fetch the next page from the server.
  onContentMetricsPageChange(page: number): void {
    if (page === this.contentMetricsPage) { return; }
    this.contentMetricsPage = page;
    this.load();
  }

  onMediaEngagementPageChange(page: number): void {
    if (page === this.mediaEngagementPage) { return; }
    this.mediaEngagementPage = page;
    this.load();
  }

  // ── Display helpers ─────────────────────────────────────────────
  mediaTypeLabel(type: MediaType | string): string {
    const labels: { [k: string]: string } = {
      video: 'Video',
      audio: 'Audio',
      pdf: 'PDF',
      image: 'Image',
      document: 'Document',
      article: 'Article',
    };
    return labels[type] || String(type || '');
  }

  // Always include the canonical media types in the dropdown so the
  // filter doesn't disappear when the active tangram has only one type
  // of media. Order matches MEDIA_TYPE_ORDER on the backend.
  get mediaTypeOptions(): string[] {
    return ['all', 'video', 'audio', 'pdf', 'image', 'document', 'article'];
  }

  get mediaTypeSelectOptions(): { value: string; label: string }[] {
    return this.mediaTypeOptions.map((opt) => ({
      value: opt,
      label: opt === 'all' ? 'All Types' : this.mediaTypeLabel(opt),
    }));
  }

  // Title shown in the per-tangram drill-down section. Pulled from the
  // tangrams[] array rather than recomputed from the rows so it stays
  // correct even when the active tangram has zero media in the period.
  get selectedTangramTitle(): string {
    if (!this.selectedPathwayId) return 'All Tangrams';
    const match = this.tangrams.find((t) => t.pathwayId === this.selectedPathwayId);
    return match ? match.title : 'Selected Tangram';
  }

  get selectedTangramMediaCount(): number {
    if (!this.selectedPathwayId) return this.tangrams.reduce((s, t) => s + t.mediaCount, 0);
    const match = this.tangrams.find((t) => t.pathwayId === this.selectedPathwayId);
    return match ? match.mediaCount : 0;
  }

  // ── Derived views ───────────────────────────────────────────────
  // Pagination is now server-side, so `contentMetrics` and
  // `mediaEngagement` are already page slices when they reach the
  // template. The remaining client-side helpers handle the optional
  // media-type filter (current page only) and the small mediaType
  // rollup table (still paginated client-side — it's at most ~6 rows).

  get filteredContentMetrics(): ContentMetricsRow[] {
    if (this.selectedMediaType === 'all') return this.contentMetrics;
    return this.contentMetrics.filter((r) => r.type === this.selectedMediaType);
  }

  get filteredMediaEngagement(): ContentMetricsRow[] {
    if (this.selectedMediaType === 'all') return this.mediaEngagement;
    return this.mediaEngagement.filter((r) => r.type === this.selectedMediaType);
  }

  get sortedContentMetrics(): ContentMetricsRow[] {
    const field = this.contentSortField;
    const dir = this.contentSortDir === 'asc' ? 1 : -1;
    return [...this.filteredContentMetrics].sort((a, b) => {
      // Sort time by raw seconds, not the formatted string ("12m" vs
      // "2h 5m" wouldn't lexicographically order correctly).
      if (field === 'avgTimeSpent') {
        return ((a.avgTimeSpentSeconds || 0) - (b.avgTimeSpentSeconds || 0)) * dir;
      }
      const av = (a as any)[field];
      const bv = (b as any)[field];
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
  }

  get pagedMediaType(): ContentMetricsTypeRollup[] {
    return this.mediaTypePerformance.slice(
      (this.mediaTypePage - 1) * this.PAGE_SIZE,
      this.mediaTypePage * this.PAGE_SIZE,
    );
  }

  // Map server rows into the existing top/low callout list shape
  // (`ProfContentPerformanceRow`) so we can reuse the shared list
  // component without forking it.
  get topPerformingForList(): ProfContentPerformanceRow[] {
    return this.topPerformingContent.map((r) => ({
      title: r.title,
      type: this.mediaTypeLabel(r.type),
      engagementScore: r.completionRate,
      views: r.views,
      completionRate: r.completionRate,
      status: 'top',
    }));
  }

  get lowPerformingForList(): ProfContentPerformanceRow[] {
    return this.lowPerformingContent.map((r) => ({
      title: r.title,
      type: this.mediaTypeLabel(r.type),
      engagementScore: r.completionRate,
      views: r.views,
      completionRate: r.completionRate,
      status: 'low',
    }));
  }

  // ── Sort handlers ───────────────────────────────────────────────
  sortContentBy(field: ContentSortKey): void {
    if (this.contentSortField === field) {
      this.contentSortDir = this.contentSortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.contentSortField = field;
      this.contentSortDir = 'desc';
    }
    this.contentMetricsPage = 1;
  }

  sortIcon(field: ContentSortKey): string {
    if (this.contentSortField !== field) return '↕';
    return this.contentSortDir === 'asc' ? '↑' : '↓';
  }

  // ── Loading ─────────────────────────────────────────────────────
  private load(): void {
    if (this.fetchSub) {
      this.fetchSub.unsubscribe();
    }
    // First load → tab-level skeleton; later fetches (page/filter
    // changes) only swap the table rows so the rest of the tab stays
    // painted.
    const isFirstLoad = this.contentMetrics.length === 0
      && this.mediaEngagement.length === 0
      && this.contentMetricsTotal === 0
      && this.mediaEngagementTotal === 0;
    if (isFirstLoad) { this.loading = true; } else { this.pageLoading = true; }
    this.error = '';

    // The per-media table and the engagement-only table page
    // independently in the UI, but the backend bundles both with one
    // page index. Use whichever the user just changed; otherwise the
    // engagement table re-uses the main page index.
    const requestedPage = Math.max(this.contentMetricsPage, this.mediaEngagementPage);

    this.fetchSub = this.analyticsService
      .getContentMetricsAnalytics(
        this.period,
        this.selectedPathwayId,
        this.startDate,
        this.endDate,
        requestedPage,
        this.PAGE_SIZE,
      )
      .subscribe(
        (data: ContentMetricsAnalyticsResponse) => {
          this.tangrams = data.tangrams || [];
          this.contentMetrics = data.contentMetrics || [];
          this.contentMetricsTotal = typeof data.contentMetricsTotal === 'number'
            ? data.contentMetricsTotal
            : this.contentMetrics.length;
          this.mediaEngagement = data.mediaEngagement || [];
          this.mediaEngagementTotal = typeof data.mediaEngagementTotal === 'number'
            ? data.mediaEngagementTotal
            : this.mediaEngagement.length;
          this.mediaTypePerformance = data.mediaTypePerformance || [];
          this.topPerformingContent = data.topPerformingContent || [];
          this.lowPerformingContent = data.lowPerformingContent || [];
          this.topQuestions = data.topQuestions || [];
          this.summary = data.summary || this.summary;
          if (data.criteria) {
            this.criteria = data.criteria;
          }
          // If the server fell back to "all" because the supplied id
          // wasn't owned by the professor, keep the dropdown honest.
          if (data.filter && typeof data.filter.pathwayId === 'string') {
            this.selectedPathwayId = data.filter.pathwayId;
          }
          // Publish to the per-tab export cache (see service comment).
          this.analyticsService.setLatestTabData('content-performance', data);
          this.loading = false;
          this.pageLoading = false;
        },
        (err) => {
          this.error = (err && err.message) ? err.message : 'Failed to load Content metrics.';
          this.loading = false;
          this.pageLoading = false;
        },
      );
  }
}
